import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  ProviderSelfHarmTaskArguments,
  ProviderTaskType,
  ProviderTriageRiskTaskArguments,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckIcon'
import { FlagIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/FlagIcon'
import { InfoIconStroke } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIconStroke'
import { useGetRelativeDate } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useGetRelativeDate'
import { TaskListPanel } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/taskListPanel/TaskListPanel'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { actions as mixpanelActions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getClientFullName } from '../../../common/utils/utils'
import { getRiskAlertTaskId } from '../../../data/appSelectors'
import { setRiskAlertTaskId } from '../../../data/appSlice'
import { useAppDispatch } from '../../../data/store'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { MarkRiskAddressedModal } from '../components/MarkRiskAddressedModal'
import RiskAlertPopover from '../components/RiskAlertPopover'
import { useMarkRiskAddressed } from '../hooks/useMarkRiskAddressed'
import { useNavigateToGeneralNote } from '../hooks/useNavigateToGeneralNote'
import { useOpenItemsFilterSections } from '../hooks/useOpenItemsFilterSections'
import { useRiskAlertPopoverData } from '../hooks/useRiskAlertPopover'
import { useSharedItemHoverActions } from '../hooks/useSharedItemHoverActions'
import { FilterableItem } from '../types'

export type RiskAlertItem = FilterableItem & {
  taskId: string
  taskType: ProviderTaskType
  taskArguments?: ProviderTriageRiskTaskArguments | ProviderSelfHarmTaskArguments
  resourceId?: string | null
}
type RiskAlertsPanelProps = {
  riskAlerts: RiskAlertItem[] | null
  isLoading: boolean
  onItemCountChange: (count: number) => void
}

const RiskAlertsPanel: React.FC<RiskAlertsPanelProps> = ({ riskAlerts, isLoading, onItemCountChange }) => {
  const getRelativeDate = useGetRelativeDate({ includeTimeForAllDates: true })
  const { isPreferredNameEnabled } = useFlags()
  const isDAProvider = useIsEngageDAProvider()
  const { sharedSections, clearSharedFilters, applyFilters } = useOpenItemsFilterSections('risk_alerts')
  const getSharedHoverActions = useSharedItemHoverActions('risk_alerts')
  const intl = useIntl()
  const { colors } = useTheme() as ThemeType
  const dispatch = useAppDispatch()
  const riskAlertTaskId = useSelector(getRiskAlertTaskId)
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(riskAlertTaskId)
  useEffect(() => {
    if (riskAlertTaskId) {
      dispatch(setRiskAlertTaskId(null))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { navigateToGeneralNote } = useNavigateToGeneralNote()
  const [selectedAlert, setSelectedAlert] = useState<RiskAlertItem | null>(null)
  const { markRiskAddressed, isLoading: isMarkingAddressed } = useMarkRiskAddressed()

  const handleConfirmMarkAddressed = useCallback(
    async (keepBadgeVisible: boolean) => {
      if (!selectedAlert) return
      const success = await markRiskAddressed(
        { taskId: selectedAlert.taskId, taskType: selectedAlert.taskType, clientId: selectedAlert.client.id },
        keepBadgeVisible,
      )
      if (success) setSelectedAlert(null)
    },
    [markRiskAddressed, selectedAlert],
  )

  const openAlert = useMemo(
    () => (riskAlerts ?? []).find((item) => item.taskId === openPopoverId) ?? null,
    [riskAlerts, openPopoverId],
  )
  const { data: popoverData } = useRiskAlertPopoverData(openAlert?.client.id ?? null)

  const getRiskHoverActions = useCallback(
    (item: RiskAlertItem) => [
      ...getSharedHoverActions(item.client),
      {
        id: 'mark-addressed',
        icon: <CheckIcon size={24} fillColor={colors.iconDefault} withCircle={false} />,
        label: intl.formatMessage({
          defaultMessage: 'Mark as addressed',
          description: 'Hover action to mark a risk alert as addressed',
        }),
        testId: tID('RiskAlertsPanel-mark-addressed'),
        onPress: () => setSelectedAlert(item),
      },
      {
        id: 'risk-details',
        icon: <InfoIconStroke fillColor={colors.iconDefault} />,
        label: intl.formatMessage({
          defaultMessage: 'More info',
          description: 'Hover action to open risk details popover',
        }),
        testId: tID('RiskAlertsPanel-risk-details'),
        onPress: () => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.OPEN_ITEMS_HOVER_ACTION,
            properties: { panel: 'risk_alerts', hover_action: 'risk_details', origin: 'hover' },
          })
          setOpenPopoverId(item.taskId)
        },
      },
    ],
    [colors.iconDefault, getSharedHoverActions, intl],
  )

  const riskAlertItems = useMemo(
    () =>
      applyFilters(riskAlerts ?? []).map((item) => {
        const isChrtHighRisk = item.taskType === ProviderTaskType.CHRT_HIGH_RISK
        return {
          icon: <FlagIcon />,
          taskType: isChrtHighRisk ? (
            <BodyText
              color={colors.textError}
              text={
                <FormattedMessage defaultMessage='CHRT risk' description='Task type label for CHRT high-risk alerts' />
              }
            />
          ) : (
            <FormattedMessage defaultMessage='Risk alert' description='Task type label' />
          ),
          title: getClientFullName(item.client, isPreferredNameEnabled),
          rightText: getRelativeDate(item.date ? new Date(item.date) : null),
          hoverActions: getRiskHoverActions(item),
          testId: tID('RiskAlertsPanel-item'),
          popover: {
            title: isChrtHighRisk ? (
              <FormattedMessage
                defaultMessage='CHRT risk'
                description='Risk alert popover title for CHRT high-risk source'
              />
            ) : isDAProvider ? (
              <FormattedMessage defaultMessage='Risk alert' description='Risk alert popover title for DA provider' />
            ) : (
              <FormattedMessage
                defaultMessage='Suicidal ideation and intent'
                description='Risk alert popover title for LCx provider'
              />
            ),
            content:
              openPopoverId === item.taskId && openAlert ? (
                <RiskAlertPopover
                  alert={{ ...openAlert, eventDate: openAlert.date }}
                  data={popoverData}
                  clientId={openAlert.client.id}
                  onMarkAddressed={() => setSelectedAlert(openAlert)}
                  onLogNote={() => {
                    dispatch(setRiskAlertTaskId(openAlert.taskId))
                    navigateToGeneralNote(openAlert.client.id)
                  }}
                />
              ) : null,
            isOpen: openPopoverId === item.taskId,
            onOpenChange: (open: boolean) => setOpenPopoverId(open ? item.taskId : null),
            position: 'bottom right' as const,
            positionKey: popoverData.isLoading ? 'loading' : 'ready',
          },
          onPress: () => {
            track({
              event: mixpanelEvents.BUTTON_PRESS,
              action: mixpanelActions.OPEN_ITEMS_ROW_PRESS,
              properties: { panel: 'risk_alerts', task_type: item.taskType },
            })
            setOpenPopoverId(item.taskId)
          },
        }
      }),
    [
      applyFilters,
      colors.textError,
      dispatch,
      getRelativeDate,
      getRiskHoverActions,
      isDAProvider,
      isPreferredNameEnabled,
      navigateToGeneralNote,
      openAlert,
      openPopoverId,
      popoverData,
      riskAlerts,
    ],
  )

  useEffect(() => {
    onItemCountChange(riskAlertItems.length)
  }, [onItemCountChange, riskAlertItems.length])

  return (
    <>
      <TaskListPanel
        filterSections={sharedSections}
        items={riskAlertItems}
        isLoading={isLoading}
        loadingMessage={
          <FormattedMessage defaultMessage='Loading risk alerts...' description='Loading state message' />
        }
        filteredEmptyMessage={
          <FormattedMessage
            defaultMessage='No risk alerts found, remove filters to see more.'
            description='Filtered empty state message'
          />
        }
        onClearFilters={clearSharedFilters}
      />
      <MarkRiskAddressedModal
        visible={selectedAlert !== null}
        onConfirm={handleConfirmMarkAddressed}
        onClose={() => setSelectedAlert(null)}
        confirmLoading={isMarkingAddressed}
      />
    </>
  )
}

export default RiskAlertsPanel
